import React, { useState, useEffect, useRef } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Hooks
import { useNavigate } from 'react-router-dom';

// Actions
import { fetchCampaigns } from '~/redux/actions/campaign-actions';
import useSortParams from '~/hooks/useSortParams';
import useListUrlQuery from '~/hooks/useListUrlQuery';

// Utils
import { MESSAGES } from '~/utils/message';
import { CAMPAIGN_SORT_KEYS } from '~/utils/listQueryParams';

// Components
import NoCampaignsScreen from './NoCampaignsScreen';
import CampaignsTable from './Table';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';
import LimitModal from '../General/LimitModal';

// MUI Components
import { Container, Box, Grid, Typography, useMediaQuery } from '@mui/material';

// Styles
import './styles.scss';

const Campaigns = () => {
  const navigate = useNavigate();
  // Search and column sort live in the URL (the source of truth), so they survive reload and
  // back/forward navigation. `search` below is only the text currently typed in the input.
  const {
    queryString,
    parsedQuery,
    appliedSearch,
    sort,
    updateUrl: updateCampaignsUrl,
  } = useListUrlQuery(CAMPAIGN_SORT_KEYS);
  const searchApplied = Boolean(appliedSearch);

  const [search, setSearch] = useState(appliedSearch);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [limitModal, setLimitModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const dispatch = useDispatch();
  const lastRequestKeyRef = useRef(null);
  const pageSizeRef = useRef(pagination.pageSize);
  const previousModeRef = useRef(null);

  // A new sort goes to the URL; the URL effect below resets to page 1 and refetches.
  const { sortProps } = useSortParams(
    [],
    (nextSort) => updateCampaignsUrl({ sort: nextSort }),
    sort
  );

  const currentMode = useSelector((state) => state.userReducers.user.isLiveMode || false);
  const campaigns = useSelector((state) => state.campaignsReducers.campaigns);
  const rowsCount = useSelector((state) => state.campaignsReducers.rowsCount);
  const from = useSelector((state) => state.campaignsReducers.from);
  const to = useSelector((state) => state.campaignsReducers.to);
  const currentPage = useSelector((state) => state.campaignsReducers.currentPage);
  const isLoading = useSelector((state) => state.campaignsReducers.loading);
  const canCreateCampaign = useSelector(
    (state) => state.userReducers?.user?.subscribedPlan?.isDefault === false
  );
  const totalCampaignsInDB = useSelector((state) => state.campaignsReducers.totalRecordsInDB);
  const totalRevenue = useSelector((state) => state.campaignsReducers.totalRevenue);
  const totalQuantity = useSelector((state) => state.campaignsReducers.totalQuantity);
  const perPage = useSelector((state) => state.campaignsReducers.perPage);
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const getCampaigns = () => {
    dispatch(fetchCampaigns({ pagination, search: appliedSearch || null, ...sort }));
  };

  // Applying the same search again just refreshes the list.
  const handleSearch = () => {
    if (search.trim() && !updateCampaignsUrl({ search: search.trim() })) {
      getCampaigns();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Emptying the input removes an applied search from the URL (the list then shows all campaigns).
  const handleSearchChange = (event) => {
    const nextSearch = event.target.value.trimStart();

    setSearch(nextSearch);

    if (!nextSearch && appliedSearch) {
      updateCampaignsUrl({ search: '' });
    }
  };

  // Clears the typed text and any applied search. Returns true when the URL changed, in which case
  // the URL effect refetches; otherwise the caller decides whether to refresh.
  const handleClearSearch = () => {
    setSearch('');
    return updateCampaignsUrl({ search: '' });
  };

  const handleChangePage = (event, newPage) => {
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleCreateCampaign = () => {
    if (!canCreateCampaign) {
      setLimitModal(true);
      return;
    }

    navigate('/campaigns/create');
  };

  const renderLimitModal = () => (
    <LimitModal open={limitModal} onClose={() => setLimitModal(false)} resourceType="campaigns" />
  );

  useEffect(() => {
    pageSizeRef.current = pagination.pageSize;
  }, [pagination.pageSize]);

  // URL effect: runs on mount and whenever search or sort change in the URL (typing, clearing,
  // back/forward, shared link). It restores the input, goes back to page 1 and fetches once.
  useEffect(() => {
    const nextPagination = { page: 1, pageSize: pageSizeRef.current };

    setSearch(parsedQuery.search);
    setPagination(nextPagination);

    lastRequestKeyRef.current = `${queryString}|${nextPagination.page}|${nextPagination.pageSize}`;
    dispatch(
      fetchCampaigns({
        pagination: nextPagination,
        search: parsedQuery.search || null,
        ...parsedQuery.sort,
      })
    );
  }, [dispatch, parsedQuery, queryString]);

  // Pagination effect: page / page-size changes only. URL changes are fetched by the effect above,
  // which also resets the page, so this one must not react to them (no duplicate or stale-page request).
  const getCampaignsRef = useRef(getCampaigns);
  getCampaignsRef.current = getCampaigns;

  useEffect(() => {
    const requestKey = `${queryString}|${pagination.page}|${pagination.pageSize}`;
    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    lastRequestKeyRef.current = requestKey;
    getCampaignsRef.current();
    // queryString is read for the dedupe key only; it must not re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize]);

  // Switching live/test mode shows a different set of campaigns: refetch from page 1.
  useEffect(() => {
    if (previousModeRef.current === null || previousModeRef.current === currentMode) {
      previousModeRef.current = currentMode;
      return;
    }

    previousModeRef.current = currentMode;
    const nextPagination = { page: 1, pageSize: pageSizeRef.current };

    setPagination(nextPagination);
    lastRequestKeyRef.current = `${queryString}|${nextPagination.page}|${nextPagination.pageSize}`;
    dispatch(
      fetchCampaigns({ pagination: nextPagination, search: appliedSearch || null, ...sort })
    );
    // Only a mode switch should run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode]);

  useEffect(() => {
    if (campaigns.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [campaigns]);

  // No compaigns screen
  const noCampaignsScreen = () => {
    return (
      <>
        <Container maxWidth={maxWidth} className="olcEmptyStatePageContainer">
          <NoCampaignsScreen onCreateCampaign={handleCreateCampaign} />
        </Container>
        {renderLimitModal()}
      </>
    );
  };

  // Show no campaigns screen only when loading is done and no records found
  if (!isLoading && totalCampaignsInDB !== null && totalCampaignsInDB < 1) {
    return noCampaignsScreen();
  }

  return (
    <Container maxWidth={maxWidth} className="campaignsPageContainer olcPrimaryListPage">
      <Grid container className="campaignsToolbarRow">
        <Grid item lg={6} md={8} sm={12} xs={12} className="searchBtnWrapper newSearchBtnWrapper">
          <Box className="searchWrapper" sx={{ height: '40px', width: 'var(--olc-search-width)' }}>
            <OlcSearchInput
              searchApplied={searchApplied}
              type="text"
              value={search}
              onChange={handleSearchChange}
              onKeyDown={searchKeyDown}
              placeholder={MESSAGES.COMPAIGNS.SEARCH_PLACE_HOLDER}
              title={MESSAGES.COMPAIGNS.SEARCH_PLACE_HOLDER}
              onClear={handleClearSearch}
            />
          </Box>
        </Grid>
        <Grid
          item
          lg={6}
          md={4}
          sm={12}
          xs={12}
          className="campaignsActionsGrid"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Box className="campaignActionsBtnWrapper">
            <OlcButton variant="primary" onClick={handleCreateCampaign}>
              {MESSAGES.COMPAIGNS.CREATE_CAMPAIGN_BUTTON}
            </OlcButton>
          </Box>
        </Grid>
      </Grid>

      <Box className="campaignsContentCard olcPrimaryListContent">
        <CampaignsTable
          campaigns={campaigns}
          rowsCount={rowsCount}
          from={from}
          to={to}
          currentPage={currentPage}
          isLoading={isLoading}
          totalCampaignsInDB={totalCampaignsInDB}
          totalRevenue={totalRevenue}
          totalQuantity={totalQuantity}
          pagination={pagination}
          sortProps={sortProps}
          perPage={perPage}
          setPagination={setPagination}
          setAnchorElMenu={setAnchorElMenu}
          setSelectedCampaign={setSelectedCampaign}
          anchorElMenu={anchorElMenu}
          onResetSearch={handleClearSearch}
          getCampaigns={getCampaigns}
          handleChangePage={handleChangePage}
          searchApplied={searchApplied}
        />
      </Box>
      {renderLimitModal()}
    </Container>
  );
};

export default Campaigns;
